import React from 'react';
import { Link } from 'react-router-dom';

import { EmptyStateOverlay } from '@trqk/components';
import { locale } from '@trqk/utils';

import { usePaymentPlanStatus } from '../../../../hooks';
import iconUrl from '../../../../assets/svg/insights-empty-gap-analysis.svg';
import { HASHES, FULL_PATHS } from '../../../../constants';

/**
 * Shows an explanation of how to upload data and enable detections in order to view the Gap
 * Analysis report. This should only be shown to Indie Edition users, because it describes the
 * process by assuming that detection data will come from Trqk (as opposed to a bulk BMAT upload).
 */
export const GapAnalysisExplainer = (): JSX.Element => {
    const { subscriptionSettings } = usePaymentPlanStatus();
    const planType = subscriptionSettings?.subscription_plan_type ?? null;
    const isScaleUser = planType === 'CHARGEBEE_PLAN_SCALE';

    return (
        <EmptyStateOverlay height={450} width={956}>
            <div
                className="px-12 pt-12 grid"
                style={{
                    gridTemplateColumns: '84px auto',
                }}
                data-cy="gap-analysis-explainer-content"
            >
                <div className="flex items-start mt-1">
                    <img
                        src={iconUrl}
                        alt="Gap Analysis chart icon"
                        width={60}
                    />
                </div>
                <div className="space-y-4">
                    <p>
                        {locale.enUS.GAP_ANALYSIS.EXPLAINER.INTRO.THE}{' '}
                        <span className="font-semibold">
                            {
                                locale.enUS.GAP_ANALYSIS.EXPLAINER.INTRO
                                    .GAP_ANALYSIS
                            }
                        </span>{' '}
                        {!isScaleUser && (
                            <>
                                {' '}
                                {
                                    locale.enUS.GAP_ANALYSIS.EXPLAINER.INTRO
                                        .REPORT_WILL_BE_AVAILABLE.BASIC_TO_PRO
                                }
                            </>
                        )}
                        {isScaleUser && (
                            <>
                                {' '}
                                {
                                    locale.enUS.GAP_ANALYSIS.EXPLAINER.INTRO
                                        .REPORT_WILL_BE_AVAILABLE.SCALE
                                }
                            </>
                        )}
                    </p>
                    <p className="italic">
                        {locale.enUS.GAP_ANALYSIS.EXPLAINER.INTRO.TO_RUN_REPORT}
                    </p>
                    <ol className="list-decimal leading-relaxed">
                        <li>
                            <Link
                                className="underline"
                                to={({ search }) => ({
                                    hash: HASHES.MANAGE_DETECTIONS.UPLOADS,
                                    pathname: FULL_PATHS.MANAGE_DETECTIONS,
                                    search,
                                })}
                            >
                                {
                                    locale.enUS.GAP_ANALYSIS.EXPLAINER.STEP_ONE
                                        .UPLOAD_AUDIO_FILES
                                }
                            </Link>{' '}
                            {
                                locale.enUS.GAP_ANALYSIS.EXPLAINER.STEP_ONE
                                    .FOR_TRACKS_ON_AIR
                            }
                        </li>
                        <li>
                            <Link
                                className="underline"
                                to={({ search }) => ({
                                    hash: HASHES.MANAGE_DETECTIONS
                                        .DETECTION_SETTINGS,
                                    pathname: FULL_PATHS.MANAGE_DETECTIONS,
                                    search,
                                })}
                            >
                                {
                                    locale.enUS.GAP_ANALYSIS.EXPLAINER.STEP_TWO
                                        .ENABLE_DETECTION
                                }
                            </Link>{' '}
                            {
                                locale.enUS.GAP_ANALYSIS.EXPLAINER.STEP_TWO
                                    .FOR_THOSE_AUDIO_FILES
                            }
                        </li>
                        <li>
                            {
                                locale.enUS.GAP_ANALYSIS.EXPLAINER.STEP_THREE
                                    .WHEN_YOUR_PRO
                            }{' '}
                            <Link
                                className="underline"
                                to={({ search }) => ({
                                    hash: HASHES.MANAGE_STATEMENTS.UPLOADS,
                                    pathname:
                                        FULL_PATHS.MANAGE_STATEMENTS_CHARTS,
                                    search,
                                })}
                            >
                                {
                                    locale.enUS.GAP_ANALYSIS.EXPLAINER
                                        .STEP_THREE.UPLOAD_THAT_STATEMENT
                                }
                            </Link>{' '}
                            {
                                locale.enUS.GAP_ANALYSIS.EXPLAINER.STEP_THREE
                                    .TO_TRQK_IQ
                            }
                        </li>
                        <li>
                            {
                                locale.enUS.GAP_ANALYSIS.EXPLAINER.STEP_FOUR
                                    .COME_BACK
                            }
                        </li>
                    </ol>
                </div>
            </div>
        </EmptyStateOverlay>
    );
};
